import createSagaMiddleware from 'redux-saga';
import combineSectionReducers from 'combine-section-reducers';
import {
  DatenhaltungKDV,
  DoRequestResponse,
  GlobalPut,
  integrateReduxDevtoolsExtension,
  NgRefreshStore,
  PS20Store,
} from '@ps20-frontend/shop-integration-redux';
import { EinreichenResponseModel, Role, Vertragsschlussverfahren } from '@ps20/bff-models';
import { App, Immobilie, VnWechselAngaben } from '@ps20-frontend/frontend-models';
import { configureStore, Dispatch, Middleware, Tuple, UnknownAction } from '@reduxjs/toolkit';

import { Produkt, Sicherungsglaeubiger } from './model';
import { rootSaga } from './sagas/rootSaga';
import { Beratung, beratungsReducer } from './reducer/beratung.reducer';
import { vorversicherungReducer } from './reducer/vorversicherung.reducer';
import { produkteReducer } from './reducer/produkte.reducer';
import { Vermittler, vermittlerReducer } from './reducer/vermittler.reducer';
import {
  Versicherungsnehmer,
  versicherungsnehmerReducer,
} from './reducer/versicherungsnehmer.reducer';
import { sicherungsglaeubigerReducer } from './reducer/sicherungsglaeubiger.reducer';
import { Zahlung, zahlungReducer } from './reducer/zahlung.reducer';
import { vertragsschlussverfahrenReducer } from './reducer/vertragsschlussverfahren.reducer';
import { Telefonverkauf, telefonverkaufReducer } from './reducer/telefonverkauf.reducer';
import { INITIAL_STATE } from './initialState';
import { rootReducer } from './reducer/root.reducer';
import { antragsResponseReducer } from './reducer/antrags-response.reducer';
import { roleReducer } from './reducer/role.reducer';
import { sammelantragsnummerReducer } from './reducer/sammelantragsnummerReducer';
import { Vorversicherung } from './model/vorversicherung';
import { kdvReducer } from './reducer/kdv.reducer';
import { Druckstatus, druckstatusReducer } from './reducer/druckstatus.reducer';
import { effectiveZaehlstueckeReducer } from './reducer/antrag-einreichen.reducer';
import { versicherungDataReducer } from './reducer/versicherungData.reducer';
import { TransientState, transientStateReducer } from './reducer/transient-state.reducer';
import { Vorversicherer } from './data/vorversicherung';
import {
  VermieteteWohnung,
  vermieteteWohnungenReducer,
} from './reducer/vermietete-wohnungen.reducer';
import { immobilienReducer } from './reducer/immobilien.reducer';
import { unfallZaehlstueckeReducer } from './reducer/unfallZaehlstuecke.reducer';
import { maklermandatVorhandenReducer } from './reducer/maklarmandatVorhanden.reducer';
import { ThhPferdAngaben, thhPferdAngabenReducer } from './reducer/thhPferdAngaben.reducer';
import { ThhHundAngaben, thhHundAngabenReducer } from './reducer/thhHundAngaben.reducer';
import {
  MeldebehoerdeAngaben,
  meldebehoerdeAngabenReducer,
} from './reducer/meldebehoerdeAngaben-reducer';
import { TkvAngaben, tkvAngabenReducer } from './reducer/tkvAngaben.reducer';
import { TkvAngabenV2, tkvAngabenV2Reducer } from './reducer/tkvAngabenV2.reducer';
import { vnWechselAngabenReducer } from './reducer/vnWechselAngaben.reducer';
import { policeVersandReducer } from './reducer/policeVersand.reducer';
import { PoliceVersand } from './model/policeVersandArt';
import { hauptfaelligkeitReducer } from './reducer/hauptfaelligkeit.reducer';
import { hauptfaelligkeitJHPReducer } from './reducer/hauptfaelligkeitJHP.reducer';
import { mindestvertragslaufzeitReducer } from './reducer/mindestvertragslaufzeit.reducer';
import { JhpAngaben, jhpAngabenReducer } from './reducer/jhpAngaben.reducer';
import { UnfAngaben, unfAngabenReducer } from './reducer/unfAngaben.reducer';
import { fernabsatzReducer } from './reducer/fernabsatz.reducer';

export interface IAppState {
  version: number;
  versicherungsnehmer: Versicherungsnehmer;
  vorversicherungen: Array<Vorversicherung>;
  versicherungData: Array<Vorversicherer>;
  sicherungsglaeubiger: Array<Sicherungsglaeubiger>;
  zahlung: Zahlung;
  beratung: Beratung;
  vermittler: Vermittler;
  produkte: Array<Produkt>;
  unfallZaehlstuecke: number;
  effectiveZaehlstuecke: number;
  vertragsschlussverfahren: Vertragsschlussverfahren;
  telefonverkauf: Telefonverkauf;
  antragResponse: DoRequestResponse<EinreichenResponseModel> | string | null;
  role: Role | null;
  kdv: DatenhaltungKDV;
  sammelantragsnummer: string;
  druckstatus: Druckstatus;
  transientState: TransientState;
  vermieteteWohnungen: Array<VermieteteWohnung>;
  immobilien: Array<Immobilie>;
  maklermandatVorhanden: boolean;
  thhPferdAngaben: ThhPferdAngaben;
  thhHundAngaben: ThhHundAngaben;
  jhpAngaben: JhpAngaben;
  meldebehoerde: MeldebehoerdeAngaben;
  tkvAngaben: TkvAngaben;
  tkvAngabenV2: TkvAngabenV2;
  vnWechselAngaben: VnWechselAngaben;
  policeVersand: PoliceVersand;
  hauptfaelligkeit?: string;
  hauptfaelligkeitJHP?: string;
  mindestvertragslaufzeit: boolean;
  unfAngaben: UnfAngaben;
  fernabsatz: boolean | null;
  dokumentId?: string;
}

export const reducers = combineSectionReducers({
  version: (version: number = null) => version,
  beratung: beratungsReducer,
  vorversicherungen: vorversicherungReducer,
  versicherungData: versicherungDataReducer,
  produkte: produkteReducer,
  vermittler: vermittlerReducer,
  versicherungsnehmer: versicherungsnehmerReducer,
  sicherungsglaeubiger: sicherungsglaeubigerReducer,
  zahlung: zahlungReducer,
  vertragsschlussverfahren: vertragsschlussverfahrenReducer,
  telefonverkauf: telefonverkaufReducer,
  antragResponse: antragsResponseReducer,
  role: roleReducer,
  kdv: kdvReducer,
  sammelantragsnummer: sammelantragsnummerReducer,
  unfallZaehlstuecke: unfallZaehlstueckeReducer,
  effectiveZaehlstuecke: effectiveZaehlstueckeReducer,
  druckstatus: druckstatusReducer,
  transientState: transientStateReducer,
  vermieteteWohnungen: vermieteteWohnungenReducer,
  immobilien: immobilienReducer,
  maklermandatVorhanden: maklermandatVorhandenReducer,
  thhPferdAngaben: thhPferdAngabenReducer,
  thhHundAngaben: thhHundAngabenReducer,
  jhpAngaben: jhpAngabenReducer,
  meldebehoerde: meldebehoerdeAngabenReducer,
  tkvAngaben: tkvAngabenReducer,
  tkvAngabenV2: tkvAngabenV2Reducer,
  vnWechselAngaben: vnWechselAngabenReducer,
  policeVersand: policeVersandReducer,
  hauptfaelligkeit: hauptfaelligkeitReducer,
  hauptfaelligkeitJHP: hauptfaelligkeitJHPReducer,
  mindestvertragslaufzeit: mindestvertragslaufzeitReducer,
  unfAngaben: unfAngabenReducer,
  fernabsatz: fernabsatzReducer,
});

export function storeFactory(initialState: IAppState): PS20Store<IAppState> {
  const ngRefreshStore = new NgRefreshStore<IAppState>(App.SHOP_CHECKOUT);
  const sagaMiddleware = createSagaMiddleware();
  const store = configureStore({
    reducer: rootReducer(reducers),
    devTools: integrateReduxDevtoolsExtension('ps-nova-checkout-gui'),
    preloadedState: initialState,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware().concat(sagaMiddleware, ngRefreshStore.ngRefreshMiddleware) as unknown as Tuple<ReadonlyArray<Middleware<Record<string, never>, IAppState, Dispatch<UnknownAction>>>>,
  });
  sagaMiddleware.run(rootSaga);
  ngRefreshStore.setDelegate(store);
  return ngRefreshStore;
}

export const localStore: PS20Store<IAppState> = storeFactory(INITIAL_STATE);

GlobalPut.getInstance().globalEventDispatcherFunction = () => {
  return localStore.globalEventDispatcherRef;
};
